import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import AppLayout from "../components/AppLayout";
import BottomNav from "../components/BottomNav";
import { Sparkles, TrendingUp } from "lucide-react";

export default function AnalysisPage() {
  const { api } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api().get("/analysis/patterns")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Sparkles size={24} className="animate-spin text-amber-400" />
        </div>
        <BottomNav />
      </AppLayout>
    );
  }

  const emotions = data?.emotions || {};
  const tags = data?.tags || {};
  const monthlyCount = data?.monthly_count || [];
  const maxEmotion = Math.max(...Object.values(emotions), 1);
  const maxTag = Math.max(...Object.values(tags), 1);

  return (
    <AppLayout>
      <div className="px-5 pt-12 pb-24 scrollbar-hide overflow-y-auto min-h-screen">
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-white drop-shadow-lg">Analyse des rêves</h1>
          <p className="text-slate-300/80 text-sm mt-1">Patterns, émotions et tendances</p>
        </div>

        {/* Total */}
        <div data-testid="analysis-total-card" className="glass-card p-6 text-center mb-5 animate-fadeInUp">
          <p className="font-heading text-5xl font-bold text-amber-400">{data?.total_dreams || 0}</p>
          <p className="text-slate-400 text-sm mt-2">Rêves analysés</p>
        </div>

        {data?.total_dreams === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate-300 text-sm">Commencez par enregistrer des rêves pour voir vos analyses</p>
          </div>
        ) : (
          <>
            {/* Emotions */}
            {Object.keys(emotions).length > 0 && (
              <div data-testid="analysis-emotions-section" className="glass-card p-5 mb-4 animate-fadeInUp" style={{ animationDelay: "0.1s" }}>
                <p className="text-xs text-amber-400 uppercase tracking-[0.15em] font-medium mb-4">Émotions récurrentes</p>
                <div className="space-y-3">
                  {Object.entries(emotions).sort((a, b) => b[1] - a[1]).map(([em, count]) => (
                    <div key={em}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white">{em}</span>
                        <span className="text-slate-400">{count}</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-700"
                          style={{ width: `${(count / maxEmotion) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {Object.keys(tags).length > 0 && (
              <div data-testid="analysis-themes-section" className="glass-card p-5 mb-4 animate-fadeInUp" style={{ animationDelay: "0.2s" }}>
                <p className="text-xs text-purple-400 uppercase tracking-[0.15em] font-medium mb-4">Thèmes dominants</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(tags).sort((a, b) => b[1] - a[1]).map(([tag, count]) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300"
                      style={{ opacity: 0.4 + (count / maxTag) * 0.6 }}
                    >
                      {tag} ({count})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Monthly */}
            {monthlyCount.length > 0 && (
              <div data-testid="analysis-monthly-section" className="glass-card p-5 animate-fadeInUp" style={{ animationDelay: "0.3s" }}>
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp size={14} className="text-emerald-400" />
                  <p className="text-xs text-emerald-400 uppercase tracking-[0.15em] font-medium">Activité mensuelle</p>
                </div>
                <div className="flex items-end gap-2 h-24">
                  {monthlyCount.map((m) => {
                    const maxCount = Math.max(...monthlyCount.map((x) => x.count), 1);
                    const h = (m.count / maxCount) * 100;
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-[10px] text-slate-400">{m.count}</span>
                        <div
                          className="w-full bg-gradient-to-t from-emerald-500/80 to-emerald-400/60 rounded-t-md transition-all"
                          style={{ height: `${Math.max(h, 8)}%` }}
                        />
                        <span className="text-[9px] text-slate-500">{m.month.split("-")[1]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </AppLayout>
  );
}
